import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Search, Filter, Plus, Eye, Edit, Trash2, MapPin, Clock,
  Star, Users, Calendar, DollarSign, X, AlertCircle, CheckCircle,
  ChevronDown, ChevronUp, Sparkles, TrendingUp, BarChart3, Loader,
  Briefcase, ChevronLeft, ChevronRight
} from 'lucide-react';
import api from '../../../utils/Api';

const CompanyMissions = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [missions, setMissions] = useState([]);
  const [selectedMission, setSelectedMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedMission, setExpandedMission] = useState(null);

  const [filters, setFilters] = useState({
    status: '',
    educationLevel: '',
    salaryRange: '',
    skills: ''
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  const companyId = user.id;

  useEffect(() => {
    fetchMissions();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '' || filters.status || filters.educationLevel || filters.salaryRange || filters.skills) {
        fetchMissions(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, filters]);

  const fetchMissions = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(searchTerm && { search: searchTerm }),
        ...(filters.status && { status: filters.status }),
        ...(filters.educationLevel && { educationLevel: filters.educationLevel }),
        ...(filters.salaryRange && { salaryRange: filters.salaryRange }),
        ...(filters.skills && { skills: filters.skills })
      });

      const response = await api.get(`/mission/company/${companyId}?${params}`);

      setMissions(response.data.data || []);
      setPagination(response.data.pagination || { current: 1, pages: 1, total: 0 });

      if (!selectedMission && response.data.data && response.data.data.length > 0) {
        setSelectedMission(response.data.data[0]);
      }

    } catch (err) {
      setError('Erreur lors du chargement des missions');
      console.error('Error fetching missions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      educationLevel: '',
      salaryRange: '',
      skills: ''
    });
    setSearchTerm('');
  };

  const handleDelete = async (missionId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette mission ?')) {
      try {
        await api.delete(`/mission/${missionId}`);
        await fetchMissions(pagination.current);
        if (selectedMission?._id === missionId) {
          setSelectedMission(null);
        }
      } catch (err) {
        console.error('Error deleting mission:', err);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString, timeString) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'short'
    })} à ${timeString}`;
  };

  const getEducationLabel = (level) => {
    const labels = {
      'aucun': 'Aucun',
      'CAP/BEP': 'CAP/BEP',
      'Bac': 'Bac',
      'Bac+2': 'Bac+2',
      'Bac+3': 'Bac+3',
      'Bac+5': 'Bac+5',
      'Doctorat': 'Doctorat'
    };
    return labels[level] || 'Non spécifié';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'active': 'Active',
      'completed': 'Terminée',
      'cancelled': 'Annulée'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      'completed': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'cancelled': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  };

  const calculateTotalHours = (schedule) => {
    if (!schedule) return 0;
    return schedule.reduce((total, session) => {
      const start = new Date(`1970-01-01T${session.startTime}`);
      const end = new Date(`1970-01-01T${session.endTime}`);
      const hours = (end - start) / (1000 * 60 * 60);
      return total + hours;
    }, 0);
  };

  const formatDuration = (totalHours) => {
    if (totalHours === 0) return "0h";
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);

    if (minutes === 0) return `${hours}h`;
    if (hours === 0) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  };

  if (loading && missions.length === 0) {
    return (
      <div className="h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-10 w-10 text-primary-600 dark:text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Title + Stats */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-950/30 rounded-lg flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                </div>
                <div>
                  <h1 className="text-base font-bold text-gray-900 dark:text-white">Mes Missions</h1>
                </div>
              </div>

              {/* Stats inline */}
              <div className="hidden md:flex items-center gap-2 text-xs">
                <div className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <span className="text-gray-700 dark:text-gray-300">{pagination.total} missions</span>
                </div>
                <div className="px-2 py-1 bg-green-50 dark:bg-green-950/30 rounded-lg">
                  <span className="text-green-700 dark:text-green-400">
                    {missions.filter(m => m.status === 'active').length} actives
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Search + Actions */}
            <div className="flex items-center gap-2">
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher une mission..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                />
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-colors ${showFilters
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
              >
                <Filter className="h-4 w-4" />
              </button>

              <button
                onClick={() => navigate('/dashboard/missions/new-mission')}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Nouvelle Mission
              </button>
            </div>
          </div>

          {/* Filters Row */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-40 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Statut</option>
                  <option value="active">Active</option>
                  <option value="completed">Terminée</option>
                  <option value="cancelled">Annulée</option>
                </select>
                <select
                  value={filters.educationLevel}
                  onChange={(e) => handleFilterChange('educationLevel', e.target.value)}
                  className="w-40 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Niveau d'éducation</option>
                  <option value="aucun">Aucun</option>
                  <option value="CAP/BEP">CAP/BEP</option>
                  <option value="Bac">Bac</option>
                  <option value="Bac+2">Bac+2</option>
                  <option value="Bac+3">Bac+3</option>
                  <option value="Bac+5">Bac+5</option>
                  <option value="Doctorat">Doctorat</option>
                </select>
                <select
                  value={filters.salaryRange}
                  onChange={(e) => handleFilterChange('salaryRange', e.target.value)}
                  className="w-40 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Tarif horaire</option>
                  <option value="0-20">0-20 DA/h</option>
                  <option value="20-40">20-40 DA/h</option>
                  <option value="40-60">40-60 DA/h</option>
                  <option value="60+">60+ DA/h</option>
                </select>
                <button
                  onClick={clearFilters}
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5"
                >
                  <X className="h-3.5 w-3.5" />
                  Effacer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 3-Column Layout */}
      <div className={`flex overflow-hidden ${showFilters ? 'h-[calc(100vh-180px)]' : 'h-[calc(100vh-120px)]'}`}>
        {/* Left - List */}
        <div className="w-80 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
              {missions.length} missions
            </span>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {missions.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="h-8 w-8 text-gray-300 dark:text-gray-700 mx-auto mb-2" />
                <p className="text-xs text-gray-500">Aucune mission</p>
              </div>
            ) : (
              <div className="space-y-2">
                {missions.map((mission) => (
                  <button
                    key={mission._id}
                    onClick={() => setSelectedMission(mission)}
                    className={`w-full p-3 rounded-lg transition-all text-left ${selectedMission?._id === mission._id
                      ? 'bg-primary-50 dark:bg-primary-950/30 border border-primary-200 dark:border-primary-800'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent'
                      }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate flex-1">
                        {mission.title?.title || mission.title}
                      </h4>
                      <span className={`ml-2 px-2 py-0.5 rounded text-[10px] font-medium ${getStatusColor(mission.status)}`}>
                        {getStatusLabel(mission.status)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate mb-2">
                      {mission.location.city} • {mission.salaryHour} DA/h
                    </p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">{formatDate(mission.createdAt)}</span>
                      <div className="flex items-center gap-1 text-gray-500">
                        <Clock className="h-3 w-3" />
                        {formatDuration(calculateTotalHours(mission.schedule))}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {pagination.pages > 1 && (
            <div className="p-3 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                <span>Page {pagination.current}/{pagination.pages}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchMissions(pagination.current - 1)}
                  disabled={pagination.current === 1}
                  className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Préc.
                </button>
                <button
                  onClick={() => fetchMissions(pagination.current + 1)}
                  disabled={pagination.current === pagination.pages}
                  className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
                >
                  Suiv.
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Center - Details */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950">
          {selectedMission ? (
            <div className="p-6 max-w-4xl mx-auto">
              {/* Header Card */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 mb-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {selectedMission.title?.title || selectedMission.title}
                      </h2>
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(selectedMission.status)}`}>
                        {getStatusLabel(selectedMission.status)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {selectedMission.location.city}, {selectedMission.location.wilaya}
                      </div>
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        {selectedMission.salaryHour} DA/h
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {getEducationLabel(selectedMission.educationLevel)}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {formatDuration(calculateTotalHours(selectedMission.schedule))} total
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="prose dark:prose-invert max-w-none">
                  <p className="text-gray-700 dark:text-gray-300">
                    {selectedMission.description}
                  </p>
                </div>
              </div>

              {/* Skills */}
              {selectedMission.skills && selectedMission.skills.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 mb-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Compétences requises
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedMission.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary-50 dark:bg-primary-950/30 text-primary-700 dark:text-primary-400 rounded-lg text-sm font-medium border border-primary-200 dark:border-primary-800"
                      >
                        {skill.name || skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Schedule */}
              {selectedMission.schedule && selectedMission.schedule.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Planning ({selectedMission.schedule.length} sessions)
                    </h3>
                    <button
                      onClick={() => setExpandedMission(expandedMission === selectedMission._id ? null : selectedMission._id)}
                      className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                    >
                      {expandedMission === selectedMission._id ? 'Masquer' : 'Voir tout'}
                      {expandedMission === selectedMission._id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                  </div>

                  {(expandedMission === selectedMission._id || selectedMission.schedule.length <= 3) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selectedMission.schedule.map((session, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700"
                        >
                          <div className="flex-shrink-0 w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold text-xs">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatDateTime(session.date, session.startTime)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {session.startTime} - {session.endTime}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Info */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  Informations
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Date de création</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {formatDate(selectedMission.createdAt)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Dernière mise à jour</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {formatDate(selectedMission.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Briefcase className="h-16 w-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-500">Sélectionnez une mission</p>
              </div>
            </div>
          )}
        </div>

        {/* Right - Actions */}
        <div className="w-80 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto">
          {selectedMission ? (
            <div className="p-4 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Actions rapides</h3>

              <div className="space-y-2">
                <button
                  onClick={() => navigate(`/dashboard/missions/${selectedMission._id}`)}
                  className="w-full px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Eye className="h-4 w-4" />
                  Voir les détails
                </button>
                <button
                  onClick={() => navigate(`/dashboard/missions/edit/${selectedMission._id}`)}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Edit className="h-4 w-4" />
                  Modifier
                </button>
                <button
                  onClick={() => handleDelete(selectedMission._id)}
                  className="w-full px-4 py-2.5 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Trash2 className="h-4 w-4" />
                  Supprimer
                </button>
              </div>

              {/* Stats */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-3">Statistiques</h4>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Candidatures</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {selectedMission.applicationsCount || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Statut</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${getStatusColor(selectedMission.status)}`}>
                      {getStatusLabel(selectedMission.status)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Tarif</span>
                    <span className="text-gray-900 dark:text-white font-medium text-right">
                      {selectedMission.salaryHour} DA/h
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center p-4">
              <p className="text-sm text-gray-500 text-center">Sélectionnez une mission</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyMissions;
